import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import {
  ArrowLeft,
  Phone,
  Mail,
  MapPin,
  Calendar,
  User,
  Smartphone,
  Wrench,
  DollarSign,
  Package,
  FileText,
  MessageSquare,
  Printer,
  FileDown,
  History,
  Paperclip,
  Plus,
  Edit,
  Save,
  X,
  MessageCircle,
  Trash2
} from 'lucide-react';
import { useBranch } from '../../contexts/BranchContext';
import { useAuth } from '../../contexts/AuthContext';
import { sendWhatsAppMessage } from '../../utils/whatsapp';
import Select from 'react-select';

// Safe JSON parse — returns fallback (default []) on any error
const safeJsonParse = (value, fallback = []) => {
  if (Array.isArray(value)) return value;
  if (typeof value === 'object' && value !== null) return value;
  if (!value || typeof value !== 'string' || value.trim() === '') return fallback;
  try {
    return JSON.parse(value);
  } catch {
    return fallback;
  }
};

const JobDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { selectedBranch } = useBranch();
  const { currencySymbol } = useAuth();

  const [showAddPartsModal, setShowAddPartsModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [editingCost, setEditingCost] = useState(false);
  const [costData, setCostData] = useState({
    estimatedCost: 0,
    actualCost: 0,
    labourCost: 0
  });
  const [paymentData, setPaymentData] = useState({
    amount: '',
    paymentMode: 'CASH',
    referenceNumber: '',
    notes: ''
  });
  const [selectedSpare, setSelectedSpare] = useState(null);
  const [spareQuantity, setSpareQuantity] = useState(1);
  const [customSparePrice, setCustomSparePrice] = useState('');
  const [printType, setPrintType] = useState('A4'); // 'A4' or 'THERMAL'

  const [editingCustomer, setEditingCustomer] = useState(false);
  const [customerForm, setCustomerForm] = useState({
    name: '',
    phone: '',
    email: '',
    address: ''
  });

  const [editingDevice, setEditingDevice] = useState(false);
  const [deviceForm, setDeviceForm] = useState({
    deviceCategory: '',
    deviceBrand: '',
    deviceModel: '',
    deviceImei: '',
    devicePassword: '',
    boxNumber: ''
  });

  const [editingProblem, setEditingProblem] = useState(false);
  const [problemDescriptionForm, setProblemDescriptionForm] = useState('');

  // Product Creation States for dynamic popup
  const [showCreateProductModal, setShowCreateProductModal] = useState(false);
  const [productFormData, setProductFormData] = useState({
    categoryId: '',
    brand: '',
    model: '',
    name: '',
    partNumber: '',
    hsnCode: '',
    gstRate: '18',
    gstInclusive: false,
    purchaseRate: '',
    saleRate: '',
    minStockLimit: '5',
    unit: 'PCS',
    description: '',
    openingStock: '0',
    supplierId: '',
    invoiceNumber: ''
  });

  const [showQuickAddSupplier, setShowQuickAddSupplier] = useState(false);
  const [newSupplierData, setNewSupplierData] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
    contactPerson: '',
    gstNumber: ''
  });

  // Fetch categories query
  const { data: categoriesData } = useQuery('categories', async () => {
    const response = await api.get('/api/categories');
    return response.data.data;
  });

  // Fetch suppliers query
  const { data: suppliersData } = useQuery('suppliers', async () => {
    const response = await api.get('/api/suppliers');
    return response.data.data;
  });

  // Create Supplier Mutation for quick inline adding
  const createSupplierMutation = useMutation(
    async (supplierData) => {
      const response = await api.post('/api/suppliers', supplierData);
      return response.data;
    },
    {
      onSuccess: (data) => {
        toast.success('Supplier created successfully!');
        queryClient.invalidateQueries('suppliers');
        const newSupplier = data.data;
        if (newSupplier) {
          setProductFormData(prev => ({
            ...prev,
            supplierId: newSupplier.id
          }));
        }
        setShowQuickAddSupplier(false);
        setNewSupplierData({
          name: '',
          phone: '',
          email: '',
          address: '',
          contactPerson: '',
          gstNumber: ''
        });
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to create supplier');
      }
    }
  );

  // Create Product Mutation
  const createProductMutation = useMutation(
    async (productData) => {
      const response = await api.post('/api/products', productData);
      return response.data;
    },
    {
      onSuccess: (data) => {
        toast.success('Product created successfully!');
        queryClient.invalidateQueries('products');
        setShowCreateProductModal(false);
        const newProduct = data.data;
        if (newProduct) {
          setSelectedSpare({
            value: newProduct.id,
            label: `${newProduct.name} (${newProduct.brand} ${newProduct.model}) - Stock: ${newProduct.currentStock}`,
            price: newProduct.saleRate,
            stock: newProduct.currentStock
          });
          setCustomSparePrice(newProduct.saleRate.toString());
        }
        setProductFormData({
          categoryId: '',
          brand: '',
          model: '',
          name: '',
          partNumber: '',
          hsnCode: '',
          gstRate: '18',
          gstInclusive: false,
          purchaseRate: '',
          saleRate: '',
          minStockLimit: '5',
          unit: 'PCS',
          description: '',
          openingStock: '0',
          supplierId: '',
          invoiceNumber: ''
        });
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to create product');
      }
    }
  );

  const handleCreateProductSubmit = (e) => {
    e.preventDefault();
    if (!productFormData.categoryId || !productFormData.brand || !productFormData.model || !productFormData.name || !productFormData.purchaseRate) {
      toast.error('Please fill all required fields');
      return;
    }
    createProductMutation.mutate({
      ...productFormData,
      branchId: jobData?.branchId || selectedBranch?.id,
      gstRate: parseFloat(productFormData.gstRate) || 0,
      purchaseRate: parseFloat(productFormData.purchaseRate) || 0,
      saleRate: parseFloat(productFormData.saleRate) || 0,
      minStockLimit: parseInt(productFormData.minStockLimit) || 5,
      openingStock: parseInt(productFormData.openingStock) || 0,
      supplierId: productFormData.supplierId || null,
      invoiceNumber: productFormData.invoiceNumber || null
    });
  };

  // Fetch job details
  const { data: jobData, isLoading } = useQuery(
    ['job', id],
    async () => {
      const response = await api.get(`/api/jobs/${id}`);
      return response.data.data;
    },
    {
      onSuccess: (data) => {
        const currentSpareCost = data.jobItems?.reduce((sum, item) => sum + item.amount, 0) || 0;
        setCostData({
          estimatedCost: currentSpareCost + (data.labourCost || 0),
          actualCost: data.actualCost || (currentSpareCost + (data.labourCost || 0)),
          labourCost: data.labourCost || 0
        });
      }
    }
  );

  // Fetch products for adding parts
  const { data: productsData } = useQuery(['products', selectedBranch?.id], async () => {
    const response = await api.get('/api/products', {
      params: { branchId: selectedBranch?.id }
    });
    return response.data.data.products;
  }, { enabled: !!selectedBranch });

  // Get company settings for STD code
  const { data: companySettings } = useQuery(
    'company-settings',
    async () => {
      const response = await api.get('/api/settings/company');
      return response.data.data;
    }
  );

  const defaultReceiptSettings = {
    showAt: true,
    labelAt: 'AT',
    showRepairItem: true,
    labelRepairItem: 'REPAIR ITEM',
    showDefect: true,
    labelDefect: 'DEFECT',
    showRepairDetails: true,
    labelRepairDetails: 'REPAIR DETAILS',
    showRepairPrice: true,
    labelRepairPrice: 'REPAIR PRICE',
    showTotalAmount: true,
    labelTotalAmount: 'TOTAL AMOUNT',
    showTotalPaid: true,
    labelTotalPaid: 'TOTAL PAID / ADVANCE',
    showBalanceDue: true,
    labelBalanceDue: 'BALANCE DUE',
    showTerms: true,
    labelTerms: 'TERMS AND CONDITIONS*',
    showSignRecipient: true,
    labelSignRecipient: 'SIGN RECIPIENT',
    showSignClient: true,
    labelSignClient: 'SIGN CLIENT',
    showCustomerSignature: true,
    labelCustomerSignatureText: 'CUSTOMER SIGNATURE',
    showFooterMessage: true,
    footerMessage: 'Thank you for choosing My Company!',
    showDate: true,
    labelDate: 'DATE',
    showName: true,
    labelName: 'NAME',
    showPhone: true,
    labelPhone: 'PHONE',
    showBarcode: true,
    labelBarcode: 'BAR CODE MANAGEMENT',
    showAddress: true,
    labelAddress: 'Main Branch Address',
    logoSize: 'large',
    showStoreNameUnderLogo: true
  };

  const receiptConfig = (() => {
    if (companySettings?.receiptSettings) {
      try {
        const parsed = JSON.parse(companySettings.receiptSettings);
        return { ...defaultReceiptSettings, ...parsed };
      } catch (e) {
        console.error('Error parsing receipt settings:', e);
      }
    }
    return defaultReceiptSettings;
  })();

  // Update job status mutation
  const updateStatusMutation = useMutation(
    async ({ status, actualCost, notes }) => {
      const response = await api.patch(`/api/jobs/${id}/status`, {
        status,
        actualCost,
        notes
      });
      return response.data;
    },
    {
      onSuccess: () => {
        toast.success('Job status updated successfully!');
        queryClient.invalidateQueries(['job', id]);
        setShowStatusModal(false);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to update status');
      }
    }
  );

  // Update generic job fields mutation
  const updateJobMutation = useMutation(
    async (updateFields) => {
      const response = await api.put(`/api/jobs/${id}`, updateFields);
      return response.data;
    },
    {
      onSuccess: () => {
        toast.success('Job details updated successfully!');
        queryClient.invalidateQueries(['job', id]);
        setEditingDevice(false);
        setEditingProblem(false);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to update job details');
      }
    }
  );

  // Update customer details mutation
  const updateCustomerMutation = useMutation(
    async (formData) => {
      const response = await api.put(`/api/customers/${jobData.customerId}`, formData);
      return response.data;
    },
    {
      onSuccess: () => {
        toast.success('Customer details updated successfully!');
        queryClient.invalidateQueries(['job', id]);
        setEditingCustomer(false);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to update customer');
      }
    }
  );

  const startEditCustomer = () => {
    setCustomerForm({
      name: jobData?.customer?.name || '',
      phone: jobData?.customer?.phone || '',
      email: jobData?.customer?.email || '',
      address: jobData?.customer?.address || ''
    });
    setEditingCustomer(true);
  };

  const startEditDevice = () => {
    setDeviceForm({
      deviceCategory: jobData?.deviceCategory || '',
      deviceBrand: jobData?.deviceBrand || '',
      deviceModel: jobData?.deviceModel || '',
      deviceImei: jobData?.deviceImei || '',
      devicePassword: jobData?.devicePassword || '',
      boxNumber: jobData?.boxNumber || ''
    });
    setEditingDevice(true);
  };

  const startEditProblem = () => {
    setProblemDescriptionForm(jobData?.problemDescription || '');
    setEditingProblem(true);
  };

  // Update job cost mutation
  const updateCostMutation = useMutation(
    async (costData) => {
      const response = await api.put(`/api/jobs/${id}`, costData);
      return response.data;
    },
    {
      onSuccess: () => {
        toast.success('Cost updated successfully!');
        queryClient.invalidateQueries(['job', id]);
        setEditingCost(false);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to update cost');
      }
    }
  );

  // Record payment mutation
  const recordPaymentMutation = useMutation(
    async (paymentData) => {
      const response = await api.post('/api/receipts/job-payment', {
        jobId: id,
        ...paymentData
      });
      return response.data;
    },
    {
      onSuccess: () => {
        toast.success('Payment recorded successfully!');
        queryClient.invalidateQueries(['job', id]);
        setShowPaymentModal(false);
        setPaymentData({
          amount: '',
          paymentMode: 'CASH',
          referenceNumber: '',
          notes: ''
        });
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to record payment');
      }
    }
  );

  // Add parts mutation
  const addPartsMutation = useMutation(
    async (items) => {
      const response = await api.post(`/api/jobs/${id}/parts`, { items });
      return response.data;
    },
    {
      onSuccess: () => {
        toast.success('Parts added successfully!');
        queryClient.invalidateQueries(['job', id]);
        setSelectedSpare(null);
        setSpareQuantity(1);
        setCustomSparePrice('');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to add parts');
      }
    }
  );

  // Remove parts mutation
  const removePartsMutation = useMutation(
    async (itemId) => {
      const response = await api.delete(`/api/jobs/${id}/parts/${itemId}`);
      return response.data;
    },
    {
      onSuccess: () => {
        toast.success('Part removed successfully!');
        queryClient.invalidateQueries(['job', id]);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to remove part');
      }
    }
  );

  const handleRecordPayment = () => {
    if (!paymentData.amount || parseFloat(paymentData.amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    const amount = parseFloat(paymentData.amount);
    const currentSpareCost = job.jobItems?.reduce((sum, item) => sum + item.amount, 0) || 0;
    const balance = (job.actualCost || (currentSpareCost + job.labourCost)) - job.advanceAmount;

    if (amount > balance) {
      toast.error(`Amount cannot exceed balance of ${currencySymbol}${balance.toFixed(2)}`);
      return;
    }

    recordPaymentMutation.mutate({
      amount,
      paymentMode: paymentData.paymentMode,
      referenceNumber: paymentData.referenceNumber,
      notes: paymentData.notes
    });
  };

  const handleUpdateCost = () => {
    updateCostMutation.mutate(costData);
  };

  const handleSpareChange = (val) => {
    setSelectedSpare(val);
    if (val) {
      setCustomSparePrice(val.price.toString());
    } else {
      setCustomSparePrice('');
    }
  };

  const handleAddSpare = () => {
    if (!selectedSpare) {
      toast.error('Please select a spare part');
      return;
    }

    const rate = parseFloat(customSparePrice);
    if (isNaN(rate) || rate < 0) {
      toast.error('Please enter a valid price/rate');
      return;
    }

    addPartsMutation.mutate([{
      productId: selectedSpare.value,
      quantity: spareQuantity,
      rate: rate
    }]);
  };

  const handleRemovePart = (itemId) => {
    if (window.confirm('Are you sure you want to remove this part? Stock will be updated.')) {
      removePartsMutation.mutate(itemId);
    }
  };

  const handleStatusChange = (newStatus) => {
    setShowStatusModal(true);
  };

  const handleWhatsAppShare = () => {
    if (!job || !job.customer?.phone) {
      toast.error('Customer phone number not available');
      return;
    }

    const template = selectedBranch?.whatsappTemplate;
    const stdCode = companySettings?.stdCode || '+91';

    const success = sendWhatsAppMessage(job, template, stdCode, currencySymbol);

    if (success) {
      toast.success('Opening WhatsApp...');
    } else {
      toast.error('Failed to open WhatsApp');
    }
  };

  const handlePrintJob = () => {
    setPrintType('THERMAL');
    const wrapper = document.querySelector('.print-wrapper');
    if (wrapper) {
      wrapper.className = 'print-wrapper print-type-THERMAL';
    }
    setTimeout(() => {
      window.print();
    }, 50);
  };

  const handlePrintA4 = () => {
    setPrintType('A4');
    const wrapper = document.querySelector('.print-wrapper');
    if (wrapper) {
      wrapper.className = 'print-wrapper print-type-A4';
    }
    setTimeout(() => {
      window.print();
    }, 50);
  };

  const handleFormKeyDown = (e) => {
    if (e.key === 'Enter') {
      if (e.target.tagName === 'TEXTAREA' || e.target.type === 'submit') {
        return;
      }
      e.preventDefault();
      const form = e.currentTarget;
      const focusableElements = Array.from(
        form.querySelectorAll('input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled])')
      );
      const index = focusableElements.indexOf(e.target);
      if (index > -1 && index < focusableElements.length - 1) {
        focusableElements[index + 1].focus();
      }
    }
  };

  const handleDownloadPDF = () => {
    const element = document.querySelector('.a4-print-layout');
    if (!element) return;

    const runDownload = () => {
      // 1. Temporarily move element into normal document flow, fully visible
      const prevStyle = element.getAttribute('style') || '';
      element.style.cssText = `
        visibility: visible !important;
        position: relative !important;
        left: auto !important;
        top: auto !important;
        width: 190mm !important;
        background: white !important;
        color: black !important;
        display: block !important;
        margin: 0 auto !important;
        z-index: 9999 !important;
      `;

      // 2. Wait for reflow + any lazy images
      const allImages = Array.from(element.querySelectorAll('img'));
      const imagePromises = allImages.map(img => {
        if (img.complete) return Promise.resolve();
        return new Promise(res => {
          img.onload = res;
          img.onerror = res; // Don't block on broken images
        });
      });

      toast.loading('Generating PDF...', { id: 'pdf-toast' });

      Promise.all(imagePromises).then(() => {
        // Small extra tick for layout flush
        requestAnimationFrame(() => {
          const opt = {
            margin: 10,
            filename: `${job.jobNumber}-Invoice.pdf`,
            image: { type: 'jpeg', quality: 0.98 },
            html2canvas: {
              scale: 2,
              useCORS: true,
              allowTaint: true,
              logging: false,
              scrollX: 0,
              scrollY: 0,
            },
            jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
          };

          window.html2pdf().from(element).set(opt).save()
            .then(() => {
              // Restore original hidden state
              element.setAttribute('style', prevStyle);
              toast.dismiss('pdf-toast');
              toast.success('PDF downloaded!');
            })
            .catch(err => {
              console.error(err);
              element.setAttribute('style', prevStyle);
              toast.dismiss('pdf-toast');
              toast.error('PDF failed. Opening print dialog instead.');
              window.print();
            });
        });
      });
    };

    if (!window.html2pdf) {
      toast.loading('Loading PDF library...', { id: 'pdf-toast' });
      const script = document.createElement('script');
      script.src = 'https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.10.1/html2pdf.bundle.min.js';
      script.onload = () => {
        toast.dismiss('pdf-toast');
        runDownload();
      };
      script.onerror = () => {
        toast.dismiss('pdf-toast');
        toast.error('Failed to load PDF library. Opening print dialog instead.');
        window.print();
      };
      document.head.appendChild(script);
    } else {
      runDownload();
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner w-8 h-8"></div>
      </div>
    );
  }

  if (!jobData) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Job not found</p>
      </div>
    );
  }

  const job = jobData;
  const statusColors = {
    PENDING: 'bg-red-100 text-red-800',
    IN_PROGRESS: 'bg-orange-100 text-orange-800',
    COMPLETED: 'bg-green-100 text-green-800',
    DELIVERED: 'bg-purple-100 text-purple-800',
    RETURNED: 'bg-rose-100 text-rose-800',
    REENTRY: 'bg-yellow-100 text-yellow-800'
  };

  const priorityColors = {
    LOW: 'bg-blue-100 text-blue-800',
    NORMAL: 'bg-gray-100 text-gray-800',
    HIGH: 'bg-orange-100 text-orange-800',
    URGENT: 'bg-red-100 text-red-800'
  };

  const totalPaid = job.advanceAmount || 0;
  const spareCost = job.jobItems?.reduce((sum, item) => sum + item.amount, 0) || 0;
  const balance = (editingCost ? (costData.actualCost || (spareCost + costData.labourCost)) : (job.actualCost || (spareCost + job.labourCost))) - totalPaid;

  return (
    <>
      {/* Print Styles */}
      <style>{`
       @media print {
  /* Hide navigation, sidebars, headers, and main app UI */
  .no-print,
  nav,
  sidebar,
  header,
  .sidebar-container,
  .navbar-container,
  footer,
  .job-details-content {
    display: none !important;
  }

  /* CRITICAL: Remove default browser print margins */
  @page {
    margin: 8mm 10mm 10mm 10mm;
    size: A4 portrait;
  }

  /* Reset body for print */
  html, body {
    margin: 0 !important;
    padding: 0 !important;
    color: #000 !important;
    background: white !important;
    height: auto !important;
  }

  /* Remove shadows and borders for cleaner print */
  .card {
    box-shadow: none !important;
    border: 1px solid #e5e7eb !important;
    page-break-inside: avoid;
  }

  /* A4 layout — NO margin-top, start from top */
  .a4-print-layout {
    display: block !important;
    margin: 0 !important;          /* ← was 15mm, that was cutting the top */
    padding: 0 !important;
    width: 100% !important;
  }

  .thermal-print-layout {
    display: none !important;
  }

  /* THERMAL override */
  .print-type-THERMAL .a4-print-layout {
    display: none !important;
  }
  .print-type-THERMAL .thermal-print-layout {
    display: block !important;
  }

  .print-type-A4 .a4-print-layout {
    display: block !important;
  }
  .print-type-A4 .thermal-print-layout {
    display: none !important;
  }

  /* Thermal paper auto-detect */
  @media (max-width: 100mm) {
    .a4-print-layout { display: none !important; }
    .thermal-print-layout { display: block !important; }
  }

  /* Thermal specific styles */
  .thermal-print {
    width: 80mm !important;
    padding: 5mm !important;
    font-family: 'Courier New', Courier, monospace !important;
    font-size: 12px !important;
  }
  .thermal-print .text-2xl { font-size: 18px !important; }
  .thermal-print .text-lg { font-size: 14px !important; }
}

/* Hide both layouts completely when viewing on screen while preserving queryability for html2canvas */
@media screen {
  .a4-print-layout {
    visibility: hidden;
    position: absolute;
    left: -9999px;
    top: 0;
    width: 190mm;
    background: white;
  }
  .thermal-print-layout {
    position: absolute;
    left: -9999px;
    top: -9999px;
    height: 0;
    overflow: hidden;
  }
}
`}</style>

      <div className="space-y-6 job-details-content">
        {/* Header */}
        <div className="flex items-center justify-between no-print">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/jobs')}
              className="btn-secondary inline-flex items-center"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Jobs
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{job.jobNumber}</h1>
              <p className="text-sm text-gray-500">
                Created on {new Date(job.createdAt).toLocaleDateString()}
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${statusColors[job.status]}`}>
              {job.status.replace('_', ' ')}
            </span>
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${priorityColors[job.priority]}`}>
              {job.priority}
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content - Left Side */}
          <div className="lg:col-span-2 space-y-6">
            {/* Customer Information */}
            <div className="card relative group">
              <div className="flex items-center justify-between mb-4 pb-2 border-b border-gray-100">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                  <User className="w-5 h-5 mr-2 text-blue-600" />
                  Customer Information
                </h2>
                {!editingCustomer ? (
                  <button
                    onClick={startEditCustomer}
                    className="p-1.5 text-blue-650 hover:bg-blue-50 rounded-lg transition-colors no-print"
                    title="Edit Customer Info"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                ) : (
                  <div className="flex space-x-2 no-print">
                    <button
                      onClick={() => updateCustomerMutation.mutate(customerForm)}
                      className="p-1.5 text-green-600 hover:bg-green-50 rounded-lg transition-colors font-semibold flex items-center text-xs"
                      title="Save Changes"
                      disabled={updateCustomerMutation.isLoading}
                    >
                      <Save className="w-4 h-4 mr-1" /> Save
                    </button>
                    <button
                      onClick={() => setEditingCustomer(false)}
                      className="p-1.5 text-red-650 hover:bg-red-50 rounded-lg transition-colors font-semibold flex items-center text-xs"
                      title="Cancel Edit"
                    >
                      <X className="w-4 h-4 mr-1" /> Cancel
                    </button>
                  </div>
                )}
              </div>

              {editingCustomer ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Name</label>
                    <input
                      type="text"
                      value={customerForm.name}
                      onChange={(e) => setCustomerForm(prev => ({ ...prev, name: e.target.value }))}
                      className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Phone</label>
                    <input
                      type="text"
                      value={customerForm.phone}
                      onChange={(e) => setCustomerForm(prev => ({ ...prev, phone: e.target.value }))}
                      className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Email</label>
                    <input
                      type="email"
                      value={customerForm.email || ''}
                      onChange={(e) => setCustomerForm(prev => ({ ...prev, email: e.target.value }))}
                      className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Address</label>
                    <textarea
                      value={customerForm.address || ''}
                      onChange={(e) => setCustomerForm(prev => ({ ...prev, address: e.target.value }))}
                      rows={2}
                      className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Name</p>
                    <p className="font-medium text-gray-900">{job.customer.name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Phone</p>
                    <p className="font-medium text-gray-900 flex items-center">
                      <Phone className="w-4 h-4 mr-1 text-gray-400" />
                      {job.customer.phone}
                    </p>
                  </div>
                  {job.customer.email && (
                    <div>
                      <p className="text-sm text-gray-500">Email</p>
                      <p className="font-medium text-gray-900 flex items-center">
                        <Mail className="w-4 h-4 mr-1 text-gray-400" />
                        {job.customer.email}
                      </p>
                    </div>
                  )}
                  {job.customer.address && (
                    <div className="md:col-span-2">
                      <p className="text-sm text-gray-500">Address</p>
                      <p className="font-medium text-gray-900 flex items-start">
                        <MapPin className="w-4 h-4 mr-1 mt-1 text-gray-400" />
                        {job.customer.address}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Device Information */}
            <div className="card relative group">
              <div className="flex items-center justify-between mb-4 pb-2 border-b border-gray-100">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                  <Smartphone className="w-5 h-5 mr-2 text-blue-600" />
                  Device Information
                </h2>
                {!editingDevice ? (
                  <button
                    onClick={startEditDevice}
                    className="p-1.5 text-blue-650 hover:bg-blue-50 rounded-lg transition-colors no-print"
                    title="Edit Device Info"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                ) : (
                  <div className="flex space-x-2 no-print">
                    <button
                      onClick={() => updateJobMutation.mutate(deviceForm)}
                      className="p-1.5 text-green-600 hover:bg-green-50 rounded-lg transition-colors font-semibold flex items-center text-xs"
                      title="Save Changes"
                      disabled={updateJobMutation.isLoading}
                    >
                      <Save className="w-4 h-4 mr-1" /> Save
                    </button>
                    <button
                      onClick={() => setEditingDevice(false)}
                      className="p-1.5 text-red-650 hover:bg-red-50 rounded-lg transition-colors font-semibold flex items-center text-xs"
                      title="Cancel Edit"
                    >
                      <X className="w-4 h-4 mr-1" /> Cancel
                    </button>
                  </div>
                )}
              </div>

              {editingDevice ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Category</label>
                    <input
                      type="text"
                      value={deviceForm.deviceCategory}
                      onChange={(e) => setDeviceForm(prev => ({ ...prev, deviceCategory: e.target.value }))}
                      className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Brand</label>
                    <input
                      type="text"
                      value={deviceForm.deviceBrand}
                      onChange={(e) => setDeviceForm(prev => ({ ...prev, deviceBrand: e.target.value }))}
                      className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Model</label>
                    <input
                      type="text"
                      value={deviceForm.deviceModel}
                      onChange={(e) => setDeviceForm(prev => ({ ...prev, deviceModel: e.target.value }))}
                      className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">IMEI / Serial No</label>
                    <input
                      type="text"
                      value={deviceForm.deviceImei}
                      onChange={(e) => setDeviceForm(prev => ({ ...prev, deviceImei: e.target.value }))}
                      className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Password</label>
                    <input
                      type="text"
                      value={deviceForm.devicePassword}
                      onChange={(e) => setDeviceForm(prev => ({ ...prev, devicePassword: e.target.value }))}
                      className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Box Number</label>
                    <input
                      type="text"
                      value={deviceForm.boxNumber}
                      onChange={(e) => setDeviceForm(prev => ({ ...prev, boxNumber: e.target.value }))}
                      className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Category</p>
                    <p className="font-medium text-gray-900">{job.deviceCategory || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Brand</p>
                    <p className="font-medium text-gray-900">{job.deviceBrand}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Model</p>
                    <p className="font-medium text-gray-900">{job.deviceModel}</p>
                  </div>
                  {job.deviceImei && (
                    <div>
                      <p className="text-sm text-gray-500">IMEI / Serial No</p>
                      <p className="font-medium text-gray-900">{job.deviceImei}</p>
                    </div>
                  )}
                  {job.devicePassword && (
                    <div>
                      <p className="text-sm text-gray-500">Password</p>
                      <p className="font-medium text-gray-900">{job.devicePassword}</p>
                    </div>
                  )}
                  {job.boxNumber && (
                    <div>
                      <p className="text-sm text-gray-500">Box Number</p>
                      <p className="font-medium text-gray-900">{job.boxNumber}</p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Problem Description */}
            <div className="card relative group">
              <div className="flex items-center justify-between mb-4 pb-2 border-b border-gray-100">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                  <Wrench className="w-5 h-5 mr-2 text-blue-600" />
                  Problem Description
                </h2>
                {!editingProblem ? (
                  <button
                    onClick={startEditProblem}
                    className="p-1.5 text-blue-650 hover:bg-blue-50 rounded-lg transition-colors no-print"
                    title="Edit Problem Description"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                ) : (
                  <div className="flex space-x-2 no-print">
                    <button
                      onClick={() => updateJobMutation.mutate({ problemDescription: problemDescriptionForm })}
                      className="p-1.5 text-green-600 hover:bg-green-50 rounded-lg transition-colors font-semibold flex items-center text-xs"
                      title="Save Changes"
                      disabled={updateJobMutation.isLoading}
                    >
                      <Save className="w-4 h-4 mr-1" /> Save
                    </button>
                    <button
                      onClick={() => setEditingProblem(false)}
                      className="p-1.5 text-red-650 hover:bg-red-50 rounded-lg transition-colors font-semibold flex items-center text-xs"
                      title="Cancel Edit"
                    >
                      <X className="w-4 h-4 mr-1" /> Cancel
                    </button>
                  </div>
                )}
              </div>

              {editingProblem ? (
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Problem Description</label>
                  <textarea
                    value={problemDescriptionForm}
                    onChange={(e) => setProblemDescriptionForm(e.target.value)}
                    rows={4}
                    className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              ) : (
                <>
                  <p className="text-gray-700 whitespace-pre-wrap">{job.problemDescription}</p>

                  {job.problems && safeJsonParse(job.problems).length > 0 && (
                    <div className="mt-4">
                      <p className="text-sm font-medium text-gray-700 mb-2">Problems List:</p>
                      <ul className="space-y-1">
                        {safeJsonParse(job.problems).map((problem, index) => (
                          <li key={index} className="flex items-center text-sm text-gray-600">
                            <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
                            {problem.text}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {job.accessories && safeJsonParse(job.accessories).length > 0 && (
                    <div className="mt-4">
                      <p className="text-sm font-medium text-gray-700 mb-2">Accessories:</p>
                      <ul className="space-y-1">
                        {safeJsonParse(job.accessories).map((accessory, index) => (
                          <li key={index} className="flex items-center text-sm text-gray-600">
                            <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                            {accessory.text}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Job Attachments Card */}
            <div className="card">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <Paperclip className="w-5 h-5 mr-2 text-blue-600" />
                Job Attachments
              </h2>
              {job.attachments && safeJsonParse(job.attachments).length > 0 ? (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {safeJsonParse(job.attachments).map((att, idx) => {
                    const url = `${api.defaults.baseURL || ''}${att.path}`;
                    const isImage = att.mimetype?.startsWith('image/') || /\.(jpg|jpeg|png|gif)$/i.test(att.filename);

                    return (
                      <div key={idx} className="border border-gray-200 rounded-lg p-2 bg-gray-50 flex flex-col justify-between">
                        {isImage ? (
                          <a href={url} target="_blank" rel="noopener noreferrer" className="block overflow-hidden rounded bg-white border border-gray-100 flex-1 min-h-[120px] flex items-center justify-center">
                            <img src={url} alt={att.originalName} className="max-h-[120px] object-contain hover:scale-105 transition-transform" />
                          </a>
                        ) : (
                          <div className="flex-1 min-h-[120px] flex items-center justify-center bg-white rounded border border-gray-100">
                            <FileText className="w-8 h-8 text-gray-400" />
                          </div>
                        )}
                        <div className="mt-2 text-xs text-gray-600 truncate" title={att.originalName}>
                          {att.originalName}
                        </div>
                        <a href={url} target="_blank" rel="noopener noreferrer" className="mt-2 text-xs text-blue-600 hover:text-blue-800 font-semibold text-center block bg-white py-1 rounded border border-blue-200">
                          View File
                        </a>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-sm text-gray-500 italic">No attachments for this job.</p>
              )}
            </div>

            {/* Add Spares Section */}
            <div className="card no-print">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <Plus className="w-5 h-5 mr-2 text-blue-600" />
                Add Spare Parts
              </h2>
              <div className="flex flex-col md:flex-row space-y-3 md:space-y-0 md:space-x-4 items-end">
                <div className="flex-1 w-full">
                  <label className="text-xs font-medium text-gray-500 mb-1 block">Search Spare (Type Name)</label>
                  <Select
                    options={productsData?.map(p => ({
                      value: p.id,
                      label: `${p.name} (${p.brand} ${p.model}) - Stock: ${p.currentStock}`,
                      price: p.saleRate,
                      stock: p.currentStock
                    }))}
                    value={selectedSpare}
                    onChange={handleSpareChange}
                    placeholder="Type to search spares..."
                    isSearchable
                    className="react-select-container"
                    classNamePrefix="react-select"
                    noOptionsMessage={() => (
                      <div className="flex flex-col items-center py-2">
                        <span className="text-sm text-gray-500 mb-2">No spare parts found</span>
                        <button
                          type="button"
                          onClick={() => setShowCreateProductModal(true)}
                          className="btn-primary py-1 px-3 text-xs bg-orange-600 hover:bg-orange-700 text-white rounded font-bold"
                        >
                          + Add Product
                        </button>
                      </div>
                    )}
                  />
                </div>
                <div className="w-full md:w-32">
                  <label className="text-xs font-medium text-gray-500 mb-1 block">Price ({currencySymbol})</label>
                  <input
                    type="number"
                    step="0.01"
                    value={customSparePrice}
                    onChange={(e) => setCustomSparePrice(e.target.value)}
                    disabled={!selectedSpare}
                    className="input-field"
                    placeholder="Price"
                  />
                </div>
                <div className="w-full md:w-24">
                  <label className="text-xs font-medium text-gray-500 mb-1 block">Qty</label>
                  <input
                    type="number"
                    min="1"
                    value={spareQuantity}
                    onChange={(e) => setSpareQuantity(parseInt(e.target.value) || 1)}
                    disabled={!selectedSpare}
                    className="input-field"
                  />
                </div>
                <div className="w-full md:w-32">
                  <label className="text-xs font-medium text-gray-500 mb-1 block">Total ({currencySymbol})</label>
                  <input
                    type="text"
                    value={((parseFloat(customSparePrice) || 0) * spareQuantity).toFixed(2)}
                    disabled
                    className="input-field bg-gray-50 text-gray-700 font-semibold"
                  />
                </div>
                <button
                  onClick={handleAddSpare}
                  disabled={!selectedSpare || addPartsMutation.isLoading}
                  className="btn-primary whitespace-nowrap h-[42px]"
                >
                  {addPartsMutation.isLoading ? 'Adding...' : 'Add Spare'}
                </button>
              </div>
              {selectedSpare && (
                <div className="mt-3 p-2 bg-blue-50 rounded text-sm text-blue-800 flex justify-between">
                  <span>Selling Rate: <strong>{currencySymbol}{selectedSpare.price.toFixed(2)}</strong></span>
                  <span>Available Stock: <strong>{selectedSpare.stock}</strong></span>
                </div>
              )}
            </div>

            {/* Parts Changed/Repaired */}
            {job.jobItems && job.jobItems.length > 0 && (
              <div className="card">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                    <Package className="w-5 h-5 mr-2 text-blue-600" />
                    Parts Used
                  </h2>
                </div>
                <div className="space-y-3">
                  {job.jobItems.map((item, index) => (
                    <div key={item.id} className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                      <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                        <span className="text-sm font-semibold text-blue-600">{index + 1}</span>
                      </div>
                      <div className="flex-1">
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="font-medium text-gray-900">{item.product.name}</p>
                            <p className="text-sm text-gray-600 mt-1">
                              {item.product.brand} {item.product.model}
                            </p>
                          </div>
                          <div className="text-right flex flex-col items-end">
                            <div className="flex items-center space-x-2">
                              <p className="font-semibold text-gray-900">{currencySymbol}{item.amount.toFixed(2)}</p>
                              <button
                                onClick={() => handleRemovePart(item.id)}
                                className="p-1 text-red-500 hover:bg-red-50 rounded no-print"
                                title="Remove Part"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                            <p className="text-xs text-gray-500">Qty: {item.quantity} × {currencySymbol}{item.rate.toFixed(2)}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-700">Total Parts Cost</span>
                      <span className="text-lg font-bold text-gray-900">
                        {currencySymbol}{job.jobItems.reduce((sum, item) => sum + item.amount, 0).toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}

          </div>

          {/* Right Sidebar */}
          <div className="space-y-6">
            {/* Cost Summary */}
            <div className="card">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                  <DollarSign className="w-5 h-5 mr-2 text-blue-600" />
                  Cost Summary
                </h2>
                {!editingCost ? (
                  <button
                    onClick={() => setEditingCost(true)}
                    className="text-blue-600 hover:text-blue-800"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                ) : (
                  <div className="flex space-x-2">
                    <button
                      onClick={handleUpdateCost}
                      className="text-green-600 hover:text-green-800"
                    >
                      <Save className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => {
                        setEditingCost(false);
                        setCostData({
                          estimatedCost: spareCost + (job.labourCost || 0),
                          actualCost: job.actualCost || (spareCost + (job.labourCost || 0)),
                          labourCost: job.labourCost || 0
                        });
                      }}
                      className="text-red-600 hover:text-red-800"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Spare Cost</span>
                  <span className="font-medium text-gray-900">{currencySymbol}{spareCost.toFixed(2)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600"> Charges</span>
                  {editingCost ? (
                    <input
                      type="number"
                      value={costData.labourCost}
                      onChange={(e) => {
                        const newLabour = parseFloat(e.target.value) || 0;
                        setCostData({
                          estimatedCost: spareCost + newLabour,
                          actualCost: spareCost + newLabour,
                          labourCost: newLabour
                        });
                      }}
                      className="input-field w-24 text-right"
                      step="0.01"
                    />
                  ) : (
                    <span className="font-medium text-gray-900">{currencySymbol}{job.labourCost.toFixed(2)}</span>
                  )}
                </div>
                <div className="flex justify-between items-center pt-2 border-t border-dashed">
                  <span className="text-sm font-semibold text-gray-900">Grand Total</span>
                  {editingCost ? (
                    <input
                      type="number"
                      value={costData.actualCost}
                      disabled
                      className="input-field w-24 text-right bg-gray-50 text-gray-700 font-semibold"
                      step="0.01"
                    />
                  ) : (
                    <span className="font-bold text-gray-950">{currencySymbol}{(job.actualCost || (spareCost + job.labourCost)).toFixed(2)}</span>
                  )}
                </div>
                <div className="border-t pt-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Advance Paid</span>
                    <span className="font-medium text-green-600">{currencySymbol}{totalPaid.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between items-center mt-2">
                    <span className="text-sm font-medium text-gray-900">Balance</span>
                    <span className={`font-bold text-lg ${balance > 0 ? 'text-red-600' : 'text-green-600'}`}>
                      {currencySymbol}{balance.toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

           {/* Payment Section */}
            {balance > 0 && (
              <div className="card bg-orange-50 border-orange-200">
                <h2 className="text-lg font-semibold text-orange-900 mb-4">Pending Payment</h2>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-orange-700">Balance Due</span>
                    <span className="text-2xl font-bold text-orange-900">{currencySymbol}{balance.toFixed(2)}</span>
                  </div>
                  <button
                    onClick={() => {
                      setPaymentData(prev => ({
                        ...prev,
                        amount: balance % 1 === 0 ? balance.toString() : balance.toFixed(2)
                      }));
                      setShowPaymentModal(true);
                    }}
                    className="w-full btn-primary text-left inline-flex items-center bg-orange-600 hover:bg-orange-700"
                  >
                    <DollarSign className="w-4 h-4 mr-2" />
                    Amount Pay
                  </button>
                </div>
              </div>
            )}
            {/* Status Actions */}
            <div className="card">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Update Status</h2>
              <div className="space-y-2">
                {job.status === 'DELIVERED' ? (
                  <button
                    onClick={() => updateStatusMutation.mutate({ status: 'REENTRY' })}
                    className="w-full btn-secondary text-left text-orange-600 bg-orange-50 hover:bg-orange-100 border-orange-200"
                  >
                    Mark as Re-Entry
                  </button>
                ) : (
                  <button
                    onClick={() => updateStatusMutation.mutate({ status: 'IN_PROGRESS' })}
                    disabled={job.status === 'IN_PROGRESS' || job.status === 'COMPLETED' || job.status === 'RETURNED' || job.status === 'REENTRY'}
                    className="w-full btn-secondary text-left disabled:opacity-50"
                  >
                    Mark as In Progress
                  </button>
                )}
                <button
                  onClick={() => updateStatusMutation.mutate({ status: 'COMPLETED' })}
                  disabled={job.status === 'COMPLETED' || job.status === 'DELIVERED' || job.status === 'RETURNED'}
                  className="w-full btn-secondary text-left disabled:opacity-50"
                >
                  Mark as Completed
                </button>
                <button
                  onClick={() => {
                    if (balance > 0) {
                      // Play alert sound
                      try {
                        const ctx = new (window.AudioContext || window.webkitAudioContext)();
                        const osc = ctx.createOscillator();
                        const gain = ctx.createGain();
                        osc.connect(gain);
                        gain.connect(ctx.destination);
                        osc.frequency.value = 440;
                        osc.type = 'sine';
                        gain.gain.setValueAtTime(0.5, ctx.currentTime);
                        gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.5);
                        osc.start(ctx.currentTime);
                        osc.stop(ctx.currentTime + 0.5);
                      } catch (e) { /* sound not supported */ }
                      // toast.error(`Please collect the balance payment of ${currencySymbol}${balance.toFixed(2)} before marking as Delivered!`, { duration: 5000 });
                      setPaymentData(prev => ({
                        ...prev,
                        amount: balance % 1 === 0 ? balance.toString() : balance.toFixed(2)
                      }));
                      setShowPaymentModal(true);
                      return;
                    }
                    updateStatusMutation.mutate({ status: 'DELIVERED' });
                  }}
                  disabled={job.status === 'DELIVERED' || job.status === 'RETURNED'}
                  className="w-full btn-primary text-left disabled:opacity-50"
                >
                  Mark as Delivered
                </button>
                <button
                  onClick={() => updateStatusMutation.mutate({ status: 'RETURNED' })}
                  disabled={job.status === 'DELIVERED' || job.status === 'RETURNED'}
                  className="w-full btn-secondary text-left text-red-600 bg-red-50 hover:bg-red-100 border-red-200 disabled:opacity-50"
                >
                  Mark as Returned
                </button>
              </div>
            </div>

 

            {/* Action Buttons */}
            <div className="card no-print">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Actions</h2>
              <div className="space-y-2">
                <button
                  onClick={handleWhatsAppShare}
                  className="w-full btn-secondary text-left inline-flex items-center bg-green-50 hover:bg-green-100 text-green-700 border-green-200"
                >
                  <MessageCircle className="w-4 h-4 mr-2" />
                  Send WhatsApp
                </button>
                <button
                  onClick={handlePrintA4}
                  className="w-full btn-secondary text-left inline-flex items-center"
                >
                  <Printer className="w-4 h-4 mr-2" />
                  Print Invoice (A4/A5)
                </button>
                <button
                  onClick={handleDownloadPDF}
                  className="w-full btn-secondary text-left inline-flex items-center"
                >
                  <FileDown className="w-4 h-4 mr-2" />
                  Download PDF (A4/A5)
                </button>
                <button
                  onClick={handlePrintJob}
                  className="w-full btn-secondary text-left inline-flex items-center"
                >
                  <Printer className="w-4 h-4 mr-2" />
                  Thermal Print (3")
                </button>
                {job.attachments && safeJsonParse(job.attachments).length > 0 && (
                  <button className="w-full btn-secondary text-left inline-flex items-center">
                    <Paperclip className="w-4 h-4 mr-2" />
                    View Attachments ({safeJsonParse(job.attachments).length})
                  </button>
                )}
              </div>
            </div>

            {/* Technician Info */}
            {job.technicianName && (
              <div className="card">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Technician</h2>
                <div className="space-y-2">
                  {job.technicianName.startsWith('__MULTI__') ? (
                    (() => {
                      try {
                        const techs = JSON.parse(job.technicianName.replace('__MULTI__', ''));
                        return Array.isArray(techs) ? techs.map((tech, idx) => (
                          <div key={idx} className="flex items-center space-x-2 p-2 bg-gray-50 rounded-lg border border-gray-100 mb-2">
                            <span className="w-6 h-6 bg-blue-100 text-blue-700 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">
                              {idx + 1}
                            </span>
                            <div>
                              <p className="font-medium text-gray-900 text-sm">{tech.name}</p>
                              {tech.phone && <p className="text-xs text-gray-500">{tech.phone}</p>}
                              {tech.specialization && <p className="text-xs text-gray-400">{tech.specialization}</p>}
                            </div>
                          </div>
                        )) : <p className="font-medium text-gray-900">{job.technicianName}</p>;
                      } catch (e) {
                        return <p className="font-medium text-gray-900">{job.technicianName}</p>;
                      }
                    })()
                  ) : (
                    <>
                      <p className="text-sm text-gray-600">Name</p>
                      <p className="font-medium text-gray-900">{job.technicianName}</p>
                    </>
                  )}
                  {job.technicianContact && !job.technicianName.startsWith('__MULTI__') && (
                    <>
                      <p className="text-sm text-gray-600 mt-3">Contact</p>
                      <p className="font-medium text-gray-900">{job.technicianContact}</p>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Dates */}
            <div className="card">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <Calendar className="w-5 h-5 mr-2 text-blue-600" />
                Important Dates
              </h2>
              <div className="space-y-3">
                <div>
                  <p className="text-sm text-gray-600">Received Date</p>
                  <p className="font-medium text-gray-900">
                    {new Date(job.receivedDate).toLocaleDateString()}
                  </p>
                </div>
                {job.expectedDate && (
                  <div>
                    <p className="text-sm text-gray-600">Expected Date</p>
                    <p className="font-medium text-gray-900">
                      {new Date(job.expectedDate).toLocaleDateString()}
                    </p>
                  </div>
                )}
                {job.completedDate && (
                  <div>
                    <p className="text-sm text-gray-600">Completed Date</p>
                    <p className="font-medium text-gray-900">
                      {new Date(job.completedDate).toLocaleDateString()}
                    </p>
                  </div>
                )}
                {job.deliveredDate && (
                  <div>
                    <p className="text-sm text-gray-600">Delivered Date</p>
                    <p className="font-medium text-gray-900">
                      {new Date(job.deliveredDate).toLocaleDateString()}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Payment Modal */}
        {showPaymentModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 no-print">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
              <div className="bg-gradient-to-r from-green-600 to-green-700 text-white px-6 py-4 flex items-center justify-between rounded-t-lg">
                <h2 className="text-xl font-bold">Record Payment</h2>
                <button
                  onClick={() => setShowPaymentModal(false)}
                  className="text-white hover:bg-white hover:bg-opacity-20 p-2 rounded-lg"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="p-6 space-y-4">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm text-blue-700">Total Amount</span>
                    <span className="text-lg font-bold text-blue-900">
                      {currencySymbol}{(job.actualCost || (spareCost + job.labourCost)).toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm text-green-700">Already Paid</span>
                    <span className="text-lg font-semibold text-green-900">
                      {currencySymbol}{totalPaid.toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center pt-2 border-t border-blue-200">
                    <span className="text-sm font-medium text-red-700">Balance Due</span>
                    <span className="text-xl font-bold text-red-900">
                      {currencySymbol}{balance.toFixed(2)}
                    </span>
                  </div>
                </div>

                <div>
                  <label className="form-label">Amount to Pay *</label>
                  <input
                    type="number"
                    className="input-field"
                    value={paymentData.amount}
                    onChange={(e) => setPaymentData({ ...paymentData, amount: e.target.value })}
                    placeholder="0.00"
                    step="0.01"
                    max={balance}
                    required
                  />
                </div>

                <div>
                  <label className="form-label">Payment Mode *</label>
                  <select
                    className="input-field"
                    value={paymentData.paymentMode}
                    onChange={(e) => setPaymentData({ ...paymentData, paymentMode: e.target.value })}
                    required
                  >
                    <option value="CASH">Cash</option>
                    <option value="BANK">Bank Transfer</option>
                    <option value="UPI">UPI / GPay</option>
                    <option value="CARD">Card</option>
                  </select>
                </div>

                <div>
                  <label className="form-label">Reference Number</label>
                  <input
                    type="text"
                    className="input-field"
                    value={paymentData.referenceNumber}
                    onChange={(e) => setPaymentData({ ...paymentData, referenceNumber: e.target.value })}
                    placeholder="Transaction ID / Cheque No"
                  />
                </div>

                <div>
                  <label className="form-label">Notes</label>
                  <textarea
                    className="input-field"
                    rows="2"
                    value={paymentData.notes}
                    onChange={(e) => setPaymentData({ ...paymentData, notes: e.target.value })}
                    placeholder="Additional notes..."
                  />
                </div>

                <div className="flex items-center justify-end space-x-4 border-t">
                  <button
                    type="button"
                    onClick={() => setShowPaymentModal(false)}
                    className="btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleRecordPayment}
                    disabled={recordPaymentMutation.isLoading}
                    className="btn-primary text-left inline-flex items-center bg-green-600 hover:bg-green-700"
                  >
                    {recordPaymentMutation.isLoading ? (
                      <>
                        <div className="spinner mr-2"></div>
                        Recording...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        Add Payment
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Create Product Modal (Instant Popup) */}
        {showCreateProductModal && createPortal(
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] p-4 no-print animate-fade-in">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto transform transition-all">
              <div className="sticky top-0 bg-gradient-to-r from-orange-600 to-orange-700 text-white px-6 py-4 flex items-center justify-between rounded-t-xl">
                <h2 className="text-xl font-bold flex items-center">
                  <Plus className="w-5 h-5 mr-2" />
                  Add New Product
                </h2>
                <button
                  type="button"
                  onClick={() => setShowCreateProductModal(false)}
                  className="text-white hover:bg-white hover:bg-opacity-20 p-2 rounded-lg text-2xl font-bold"
                >
                  ×
                </button>
              </div>

              <form onSubmit={handleCreateProductSubmit} onKeyDown={handleFormKeyDown} className="p-6 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="form-label font-semibold text-gray-700 block mb-1">Category *</label>
                    <select
                      className="input-field border border-gray-300 rounded-lg p-2 w-full focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                      value={productFormData.categoryId}
                      onChange={(e) => setProductFormData({ ...productFormData, categoryId: e.target.value })}
                      required
                    >
                      <option value="">Select Category</option>
                      {(categoriesData || []).map(cat => (
                        <option key={cat.id} value={cat.id}>{cat.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="form-label font-semibold text-gray-700 block mb-1">Brand *</label>
                    <input
                      type="text"
                      className="input-field border border-gray-300 rounded-lg p-2 w-full focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                      value={productFormData.brand}
                      placeholder="e.g. Apple, Samsung"
                      onChange={(e) => setProductFormData({ ...productFormData, brand: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <label className="form-label font-semibold text-gray-700 block mb-1">Model *</label>
                    <input
                      type="text"
                      className="input-field border border-gray-300 rounded-lg p-2 w-full focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                      value={productFormData.model}
                      placeholder="e.g. iPhone 13 Pro"
                      onChange={(e) => setProductFormData({ ...productFormData, model: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <label className="form-label font-semibold text-gray-700 block mb-1">Item Name *</label>
                    <input
                      type="text"
                      className="input-field border border-gray-300 rounded-lg p-2 w-full focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                      value={productFormData.name}
                      placeholder="e.g. Display Combo, Battery"
                      onChange={(e) => setProductFormData({ ...productFormData, name: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <label className="form-label font-semibold text-gray-700 block mb-1">Part Number</label>
                    <input
                      type="text"
                      className="input-field border border-gray-300 rounded-lg p-2 w-full focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                      value={productFormData.partNumber}
                      placeholder="e.g. PN-12345"
                      onChange={(e) => setProductFormData({ ...productFormData, partNumber: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="form-label font-semibold text-gray-700 block mb-1">HSN Code</label>
                    <input
                      type="text"
                      className="input-field border border-gray-300 rounded-lg p-2 w-full focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                      value={productFormData.hsnCode}
                      placeholder="e.g. 847330"
                      onChange={(e) => setProductFormData({ ...productFormData, hsnCode: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="form-label font-semibold text-gray-700 block mb-1">GST % *</label>
                    <div className="flex items-center space-x-2">
                      <input
                        type="number"
                        className="input-field border border-gray-300 rounded-lg p-2 flex-1 focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                        value={productFormData.gstRate}
                        onChange={(e) => setProductFormData({ ...productFormData, gstRate: e.target.value })}
                        step="0.01"
                        required
                      />
                      <label className="flex items-center space-x-1.5 text-sm cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={productFormData.gstInclusive}
                          onChange={(e) => setProductFormData({ ...productFormData, gstInclusive: e.target.checked })}
                          className="rounded text-orange-600 focus:ring-orange-500 cursor-pointer"
                        />
                        <span>Inclusive</span>
                      </label>
                    </div>
                  </div>
                  <div>
                    <label className="form-label font-semibold text-gray-700 block mb-1">Purchase Rate *</label>
                    <input
                      type="number"
                      className="input-field border border-gray-300 rounded-lg p-2 w-full focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                      value={productFormData.purchaseRate}
                      placeholder="0.00"
                      onChange={(e) => setProductFormData({ ...productFormData, purchaseRate: e.target.value })}
                      step="0.01"
                      required
                    />
                  </div>
                  <div>
                    <label className="form-label font-semibold text-gray-700 block mb-1">Sale Rate</label>
                    <input
                      type="number"
                      className="input-field border border-gray-300 rounded-lg p-2 w-full focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                      value={productFormData.saleRate}
                      placeholder="0.00"
                      onChange={(e) => setProductFormData({ ...productFormData, saleRate: e.target.value })}
                      step="0.01"
                    />
                  </div>
                  <div>
                    <label className="form-label font-semibold text-gray-700 block mb-1">Set Limit (Min Stock) *</label>
                    <input
                      type="number"
                      className="input-field border border-gray-300 rounded-lg p-2 w-full focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                      value={productFormData.minStockLimit}
                      onChange={(e) => setProductFormData({ ...productFormData, minStockLimit: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <label className="form-label font-semibold text-gray-700 block mb-1">Unit</label>
                    <select
                      className="input-field border border-gray-300 rounded-lg p-2 w-full focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                      value={productFormData.unit}
                      onChange={(e) => setProductFormData({ ...productFormData, unit: e.target.value })}
                    >
                      <option value="PCS">PCS</option>
                      <option value="KG">KG</option>
                      <option value="LITER">LITER</option>
                      <option value="METER">METER</option>
                    </select>
                  </div>
                </div>

                {/* Opening Stock & Supplier Details Section */}
                <div className="border-t border-dashed border-gray-200 pt-4 mt-2">
                  <h3 className="text-sm font-bold text-orange-800 mb-3 flex items-center">
                    <Package className="w-4 h-4 mr-1.5" />
                    Opening Stock & Supplier Information
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="form-label font-semibold text-gray-700 block mb-1">Opening Stock Quantity</label>
                      <input
                        type="number"
                        min="0"
                        className="input-field border border-gray-300 rounded-lg p-2 w-full focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                        value={productFormData.openingStock}
                        onChange={(e) => setProductFormData({ ...productFormData, openingStock: e.target.value })}
                        placeholder="0"
                      />
                    </div>

                    <div>
                      <label className="form-label font-semibold text-gray-700 block mb-1">Bill / Invoice Number</label>
                      <input
                        type="text"
                        className="input-field border border-gray-300 rounded-lg p-2 w-full focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all disabled:opacity-50 disabled:bg-gray-100"
                        value={productFormData.invoiceNumber}
                        onChange={(e) => setProductFormData({ ...productFormData, invoiceNumber: e.target.value })}
                        placeholder="e.g. INV-1002"
                        disabled={!(parseInt(productFormData.openingStock) > 0)}
                        title={!(parseInt(productFormData.openingStock) > 0) ? "Set Opening Stock > 0 to enter Invoice" : ""}
                      />
                    </div>

                    <div className="col-span-2">
                      <label className="form-label font-semibold text-gray-700 flex items-center justify-between mb-1">
                        <span>Select Supplier</span>
                        <button
                          type="button"
                          onClick={() => setShowQuickAddSupplier(true)}
                          className="text-xs text-orange-600 hover:text-orange-800 font-bold flex items-center space-x-1 focus:outline-none"
                        >
                          <Plus className="w-3.5 h-3.5" />
                          <span>New Supplier</span>
                        </button>
                      </label>

                      <div className="flex items-center space-x-2">
                        <div className="flex-1">
                          <Select
                            options={(suppliersData || []).map(s => ({
                              value: s.id,
                              label: `${s.name} ${s.phone ? `(${s.phone})` : ''}`
                            }))}
                            value={
                              productFormData.supplierId
                                ? {
                                  value: productFormData.supplierId,
                                  label: (suppliersData || []).find(s => s.id === productFormData.supplierId)?.name || 'Selected Supplier'
                                }
                                : null
                            }
                            onChange={(selected) => setProductFormData({ ...productFormData, supplierId: selected ? selected.value : '' })}
                            placeholder="Search or Select Supplier..."
                            isClearable
                            className="react-select-container"
                            classNamePrefix="react-select"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="form-label font-semibold text-gray-700 block mb-1">Description</label>
                  <textarea
                    className="input-field border border-gray-300 rounded-lg p-2 w-full focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
                    rows="3"
                    placeholder="Spare part specifications, notes..."
                    value={productFormData.description}
                    onChange={(e) => setProductFormData({ ...productFormData, description: e.target.value })}
                  />
                </div>

                <div className="flex items-center justify-end space-x-4 pt-4 border-t">
                  <button
                    type="button"
                    onClick={() => setShowCreateProductModal(false)}
                    className="btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={createProductMutation.isLoading}
                    className="btn-primary bg-orange-600 hover:bg-orange-700 text-white font-semibold"
                  >
                    {createProductMutation.isLoading ? (
                      <>
                        <div className="spinner mr-2"></div>
                        Saving...
                      </>
                    ) : (
                      'Create Product'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>,
          document.body
        )}

        {/* Quick Add Supplier Popup Modal Overlay */}
        {showQuickAddSupplier && createPortal(
          <div className="fixed inset-0 bg-black bg-opacity-65 flex items-center justify-center z-[150] p-4 no-print animate-fade-in backdrop-blur-sm">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg transform transition-all border border-orange-100 overflow-hidden">
              {/* Modal Header */}
              <div className="bg-gradient-to-r from-orange-600 to-orange-700 text-white px-6 py-4 flex items-center justify-between">
                <h3 className="text-lg font-bold flex items-center">
                  <Plus className="w-5 h-5 mr-2" />
                  Quick Add New Supplier
                </h3>
                <button
                  type="button"
                  onClick={() => setShowQuickAddSupplier(false)}
                  className="text-white hover:bg-white hover:bg-opacity-20 px-2.5 py-1 rounded-lg text-xl font-bold transition-all animate-pulse"
                >
                  ×
                </button>
              </div>

              {/* Modal Body Form */}
              <div className="p-6 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <label className="form-label font-semibold text-gray-700 block mb-1">Supplier Name *</label>
                    <input
                      type="text"
                      className="input-field border border-gray-300 rounded-lg p-2.5 w-full focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all bg-white text-gray-900"
                      placeholder="e.g. Acme Spares Corp"
                      value={newSupplierData.name}
                      onChange={(e) => setNewSupplierData({ ...newSupplierData, name: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <label className="form-label font-semibold text-gray-700 block mb-1">Contact Person</label>
                    <input
                      type="text"
                      className="input-field border border-gray-300 rounded-lg p-2.5 w-full focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all bg-white text-gray-900"
                      placeholder="e.g. John Doe"
                      value={newSupplierData.contactPerson}
                      onChange={(e) => setNewSupplierData({ ...newSupplierData, contactPerson: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="form-label font-semibold text-gray-700 block mb-1">Phone Number</label>
                    <input
                      type="text"
                      className="input-field border border-gray-300 rounded-lg p-2.5 w-full focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all bg-white text-gray-900"
                      placeholder="e.g. +91 9876543210"
                      value={newSupplierData.phone}
                      onChange={(e) => setNewSupplierData({ ...newSupplierData, phone: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="form-label font-semibold text-gray-700 block mb-1">Email Address</label>
                    <input
                      type="email"
                      className="input-field border border-gray-300 rounded-lg p-2.5 w-full focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all bg-white text-gray-900"
                      placeholder="e.g. sales@acme.com"
                      value={newSupplierData.email}
                      onChange={(e) => setNewSupplierData({ ...newSupplierData, email: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="form-label font-semibold text-gray-700 block mb-1">GST Number</label>
                    <input
                      type="text"
                      className="input-field border border-gray-300 rounded-lg p-2.5 w-full focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all bg-white text-gray-900"
                      placeholder="e.g. 22AAAAA0000A1Z5"
                      value={newSupplierData.gstNumber}
                      onChange={(e) => setNewSupplierData({ ...newSupplierData, gstNumber: e.target.value })}
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="form-label font-semibold text-gray-700 block mb-1">Address</label>
                    <textarea
                      className="input-field border border-gray-300 rounded-lg p-2.5 w-full focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all bg-white text-gray-900"
                      rows="2"
                      placeholder="Full supplier address details..."
                      value={newSupplierData.address}
                      onChange={(e) => setNewSupplierData({ ...newSupplierData, address: e.target.value })}
                    />
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex items-center justify-end space-x-4 pt-4 border-t border-gray-100">
                  <button
                    type="button"
                    onClick={() => setShowQuickAddSupplier(false)}
                    className="px-5 py-2 border border-gray-300 hover:bg-gray-50 rounded-lg font-semibold text-gray-700 transition-all bg-gray-100"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      if (!newSupplierData.name.trim()) {
                        toast.error("Supplier Name is required");
                        return;
                      }
                      createSupplierMutation.mutate(newSupplierData);
                    }}
                    disabled={createSupplierMutation.isLoading}
                    className="bg-orange-600 hover:bg-orange-700 text-white font-semibold px-5 py-2 shadow-md flex items-center rounded-lg transition-all"
                  >
                    {createSupplierMutation.isLoading ? (
                      <>
                        <div className="spinner mr-2"></div>
                        Saving...
                      </>
                    ) : (
                      "Create Supplier"
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}
      </div>

      {/* Print Layout Wrapper with state-driven overr      {/* Print Layout Wrapper with state-driven overrides */}
      <div className={`print-wrapper print-type-${printType}`}>
        {/* A4/A5 Print Layout */}
        <div className="print-layout a4-print-layout p-8 bg-white">

          {/* 3-Column Top Header Section */}
          <div className={`grid gap-4 mb-6 ${receiptConfig.showBarcode ? 'grid-cols-3' : 'grid-cols-2'}`}>

            {/* Column 1: Customer Invoice Info */}
            <div className="border border-gray-300 rounded-xl p-4 bg-white flex flex-col justify-center text-center">
              <div className="text-gray-400 font-extrabold text-[10px] uppercase tracking-wider mb-2">
                Invoice {job.jobNumber}
              </div>
              <div className="space-y-2.5">
                {receiptConfig.showDate && (
                  <div>
                    <span className="text-[9px] font-bold text-gray-400 block tracking-widest uppercase mb-0.5">{receiptConfig.labelDate || 'DATE'}</span>
                    <span className="font-extrabold text-gray-900 text-xs">
                      {new Date(job.createdAt).toLocaleDateString('en-GB')}
                    </span>
                  </div>
                )}
                {receiptConfig.showName && (
                  <div>
                    <span className="text-[9px] font-bold text-gray-400 block tracking-widest uppercase mb-0.5">{receiptConfig.labelName || 'NAME'}</span>
                    <span className="font-extrabold text-gray-900 text-xs uppercase">{job.customer?.name}</span>
                  </div>
                )}
                {receiptConfig.showPhone && (
                  <div>
                    <span className="text-[9px] font-bold text-gray-400 block tracking-widest uppercase mb-0.5">{receiptConfig.labelPhone || 'PHONE'}</span>
                    <span className="font-extrabold text-gray-900 text-xs">{job.customer?.phone}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Column 2: Barcode & QR Code tracking */}
            {receiptConfig.showBarcode && (
              <div className="border border-gray-300 rounded-xl p-4 bg-white flex flex-col items-center justify-center text-center">
                <span className="text-[10px] font-bold text-gray-500 tracking-wider mb-1 block">{receiptConfig.labelBarcode || 'BAR CODE MANAGEMENT'}</span>


                {/* QR Code Graphic Pattern */}
                <div className="w-16 h-16 bg-white p-1 border border-gray-200 rounded-lg shadow-sm flex items-center justify-center my-1.5 overflow-hidden">
                  <img
                    src={`https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(job.customer?.phone ? `${window.location.origin}/login?phone=${job.customer.phone}` : `${window.location.origin}/login`)}`}
                    alt="QR Code"
                    className="w-full h-full object-contain"
                  />
                </div>

                <span className="text-[9px] text-gray-400 mt-1 leading-tight">
                  Check online at<br />
                  <span className="text-blue-600 underline font-semibold break-all">
                    {job.customer?.phone ? `${window.location.origin.replace(/^https?:\/\//, '')}/login?phone=${job.customer.phone}` : `${window.location.origin.replace(/^https?:\/\//, '')}/login`}
                  </span>
                </span>
              </div>
            )}

            {/* Column 3: Company Logo & Details */}
            <div className="border border-gray-300 rounded-xl p-4 bg-white flex flex-col items-center justify-center text-center">
              {(selectedBranch?.logo || companySettings?.logo) && (
                <div className={`flex items-center justify-center overflow-hidden transition-all bg-white rounded-xl p-1 mb-2 border border-gray-200 ${
                  receiptConfig.logoSize === 'large' 
                    ? 'w-24 h-24 shadow-md' 
                    : 'w-14 h-14 shadow-sm'
                }`}>
                  <img
                    src={((selectedBranch?.logo || companySettings?.logo).startsWith('http') ? (selectedBranch?.logo || companySettings?.logo) : `${api.defaults.baseURL || ''}${selectedBranch?.logo || companySettings?.logo}`)}
                    alt="Store Logo"
                    className="max-h-full max-w-full object-contain"
                    onError={(e) => { e.target.src = "https://cdn-icons-png.flaticon.com/512/2942/2942544.png"; }}
                  />
                </div>
              )}
              {receiptConfig.showStoreNameUnderLogo && (
                <h1 className="text-sm font-extrabold uppercase tracking-wide text-gray-900 leading-tight">
                  {selectedBranch?.name || companySettings?.companyName || "Selfie Mobiles"}
                </h1>
              )}
              {companySettings?.phone && (
                <p className="text-[9px] text-gray-500 font-semibold mt-0.5">
                  Phone: {companySettings.phone}
                </p>
              )}
              {receiptConfig.showAddress && (
                <p className="text-[9px] text-gray-500 font-medium leading-tight mt-2 pt-2 border-t border-gray-100 w-full">
                  {selectedBranch?.address || companySettings?.address || "125B, KAMARAJ STREET, RAPALAYAM-626117"}
                </p>
              )}
            </div>

          </div>

          {/* Invoice Repair Item Table */}
          <div className="overflow-x-auto border border-gray-300 rounded-xl mb-6">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-100/80 border-b border-gray-300 text-gray-600 text-[11px] font-bold uppercase">
                  {receiptConfig.showAt && <th className="px-4 py-3 text-center w-12">{receiptConfig.labelAt || 'At'}</th>}
                  {receiptConfig.showRepairItem && <th className="px-4 py-3">{receiptConfig.labelRepairItem || 'Repair item'}</th>}
                  {receiptConfig.showDefect && <th className="px-4 py-3">{receiptConfig.labelDefect || 'Defect'}</th>}
                  {receiptConfig.showRepairDetails && <th className="px-4 py-3">{receiptConfig.labelRepairDetails || 'Repair details'}</th>}
                  {receiptConfig.showRepairPrice && <th className="px-4 py-3 text-right">{receiptConfig.labelRepairPrice || 'Repair Price'}</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 text-xs font-medium text-gray-800">
                <tr className="hover:bg-slate-50/50">
                  {receiptConfig.showAt && <td className="px-4 py-4 text-center text-gray-400 font-bold">1</td>}
                  {receiptConfig.showRepairItem && (
                    <td className="px-4 py-4 font-black uppercase text-gray-900">
                      {job.deviceBrand} {job.deviceModel}
                    </td>
                  )}
                  {receiptConfig.showDefect && (
                    <td className="px-4 py-4 text-gray-600">
                      {job.problemDescription || "Device Diagnostics"}
                    </td>
                  )}
                  {receiptConfig.showRepairDetails && (
                    <td className="px-4 py-4 text-gray-500 italic">
                      {job.notes || "Standard hardware repair service"}
                    </td>
                  )}
                  {receiptConfig.showRepairPrice && (
                    <td className="px-4 py-4 text-right font-bold">
                      {currencySymbol}{Math.max(0, (job.actualCost || (spareCost + job.labourCost)) - spareCost - job.labourCost).toFixed(2)}
                    </td>
                  )}
                </tr>

                {/* Render any additional spare parts / inventory job items */}
                {job.jobItems && job.jobItems.map((item, idx) => (
                  <tr key={item.id} className="hover:bg-slate-50/50 text-[11px]">
                    {receiptConfig.showAt && <td className="px-4 py-3 text-center text-gray-400 font-bold">{idx + 2}</td>}
                    {receiptConfig.showRepairItem && (
                      <td className="px-4 py-3 font-semibold text-gray-700">
                        Spare: {item.product.name}
                      </td>
                    )}
                    {receiptConfig.showDefect && <td className="px-4 py-3 text-gray-500">Inventory Part Charge</td>}
                    {receiptConfig.showRepairDetails && <td className="px-4 py-3 text-gray-400">Qty: {item.quantity} @ {currencySymbol}{item.rate.toFixed(2)}</td>}
                    {receiptConfig.showRepairPrice && (
                      <td className="px-4 py-3 text-right font-bold text-gray-700">
                        {currencySymbol}{item.amount.toFixed(2)}
                      </td>
                    )}
                  </tr>
                ))}

                {/* Render Labour Charges if any */}
                {job.labourCost > 0 && (
                  <tr className="hover:bg-slate-50/50 text-[11px]">
                    {receiptConfig.showAt && (
                      <td className="px-4 py-3 text-center text-gray-400 font-bold">
                        {job.jobItems ? job.jobItems.length + 2 : 2}
                      </td>
                    )}
                    {receiptConfig.showRepairItem && (
                      <td className="px-4 py-3 font-semibold text-gray-700">
                        Labour Charges
                      </td>
                    )}
                    {receiptConfig.showDefect && <td className="px-4 py-3 text-gray-500">Service Fee</td>}
                    {receiptConfig.showRepairDetails && <td className="px-4 py-3 text-gray-400">Standard service charge</td>}
                    {receiptConfig.showRepairPrice && (
                      <td className="px-4 py-3 text-right font-bold text-gray-700">
                        {currencySymbol}{job.labourCost.toFixed(2)}
                      </td>
                    )}
                  </tr>
                )}

                {/* Spacer row for aesthetics */}
                <tr className="h-6">
                  <td colSpan={[receiptConfig.showAt, receiptConfig.showRepairItem, receiptConfig.showDefect, receiptConfig.showRepairDetails, receiptConfig.showRepairPrice].filter(Boolean).length}></td>
                </tr>

                {/* Total Cost, Paid, and Balance Summary Rows */}
                {receiptConfig.showTotalAmount && (
                  <tr className="border-t border-gray-255">
                    <td colSpan={[receiptConfig.showAt, receiptConfig.showRepairItem, receiptConfig.showDefect, receiptConfig.showRepairDetails].filter(Boolean).length} className="px-4 py-2"></td>
                    <td className="px-4 py-2 text-right text-xs font-semibold uppercase text-gray-500 tracking-wider">
                      {receiptConfig.labelTotalAmount || 'Total Amount'}
                    </td>
                    <td className="px-4 py-2 text-right text-xs font-bold text-gray-950">
                      {currencySymbol}{(job.actualCost || (spareCost + job.labourCost)).toFixed(2)}
                    </td>
                  </tr>
                )}

                {receiptConfig.showTotalPaid && (
                  <tr>
                    <td colSpan={[receiptConfig.showAt, receiptConfig.showRepairItem, receiptConfig.showDefect, receiptConfig.showRepairDetails].filter(Boolean).length} className="px-4 py-1"></td>
                    <td className="px-4 py-1 text-right text-xs font-semibold uppercase text-gray-500 tracking-wider">
                      {receiptConfig.labelTotalPaid || 'Total Paid / Advance'}
                    </td>
                    <td className="px-4 py-1 text-right text-xs font-bold text-green-600">
                      {currencySymbol}{job.advanceAmount.toFixed(2)}
                    </td>
                  </tr>
                )}

                {/* Balance Due Calculation Row */}
                {receiptConfig.showBalanceDue && (
                  <tr className="bg-slate-50 border-t-2 border-gray-300">
                    <td colSpan={[receiptConfig.showAt, receiptConfig.showRepairItem, receiptConfig.showDefect, receiptConfig.showRepairDetails].filter(Boolean).length} className="px-4 py-3"></td>
                    <td className="px-4 py-3 text-right text-xs font-extrabold uppercase text-gray-500 tracking-wider">
                      {receiptConfig.labelBalanceDue || 'Balance Due'}
                    </td>
                    <td className="px-4 py-3 text-right text-xs font-black text-gray-950 bg-slate-100">
                      {currencySymbol}{balance.toFixed(2)}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Bottom Section: Terms & Signature Grid */}
          <div className="space-y-6">

            {/* Terms and Conditions */}
            {receiptConfig.showTerms && (
              <div className="text-[11px] space-y-1 bg-slate-50 p-4 border border-gray-200 rounded-xl">
                <h5 className="font-extrabold text-[10px] text-gray-800 uppercase tracking-wider mb-2">{receiptConfig.labelTerms || 'TERMS AND CONDITIONS*'}</h5>
                <ol className="list-none space-y-1.5 text-gray-600 leading-normal pl-0">
                  {(() => {
                    if (companySettings?.terms) {
                      try {
                        const parsed = JSON.parse(companySettings.terms);
                        if (Array.isArray(parsed) && parsed.length > 0) {
                          return parsed.map((term, i) => (
                            <li key={i}>{i + 1}-{term}</li>
                          ));
                        }
                      } catch (err) { }
                    }
                    // Fallback defaults
                    return (
                      <>
                        <li>1-We will not be held Liable for any loss of Data in case of software problem</li>
                        <li>2-Collect Your Mobile within 15 days after complete your device repair</li>
                      </>
                    );
                  })()}
                </ol>
              </div>
            )}

            {/* Signature Box */}
            {(receiptConfig.showSignRecipient || receiptConfig.showSignClient) && (
              <div className="grid grid-cols-2 gap-4 border border-gray-200 rounded-xl p-5 bg-slate-50/20 text-center">
                {receiptConfig.showSignRecipient ? (
                  <div className="space-y-12 flex flex-col justify-between">
                    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block">{receiptConfig.labelSignRecipient || 'SIGN RECIPIENT'}</span>
                    <div className="border-b border-dashed border-gray-300 w-2/3 mx-auto"></div>
                  </div>
                ) : <div></div>}
                {receiptConfig.showSignClient ? (
                  <div className="space-y-12 flex flex-col justify-between">
                    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block">{receiptConfig.labelSignClient || 'SIGN CLIENT'}</span>
                    <div className="border-b border-dashed border-gray-300 w-2/3 mx-auto"></div>
                  </div>
                ) : <div></div>}
              </div>
            )}

            {receiptConfig.showCustomerSignature && (
              <p className="text-[9px] font-bold text-gray-400 text-center uppercase tracking-wider pt-2">
                {receiptConfig.labelCustomerSignatureText || 'CUSTOMER SIGNATURE'}
              </p>
            )}

          </div>

          {receiptConfig.showFooterMessage && (
            <div className="mt-8 text-center text-xs text-gray-400 italic">
              {receiptConfig.footerMessage}
            </div>
          )}
        </div>

        {/* Thermal Print Layout (3") */}
        <div className="print-layout thermal-print-layout thermal-print bg-white mx-auto">
          <div className="text-center border-b pb-4 mb-4">
            {receiptConfig.showStoreNameUnderLogo && (
              <h1 className="text-lg font-bold uppercase">{companySettings?.companyName || selectedBranch?.name}</h1>
            )}
            <p className="text-[10px]">{selectedBranch?.name} Branch</p>
            {receiptConfig.showAddress && (
              <p className="text-[10px]">{selectedBranch?.address || companySettings?.address}</p>
            )}
            <p className="text-[10px]">Ph: {selectedBranch?.phone || companySettings?.phone}</p>
            {companySettings?.gstNumber && <p className="text-[10px]">GSTIN: {companySettings.gstNumber}</p>}
          </div>

          <div className="text-[10px] space-y-1 mb-4 border-b pb-2">
            <div className="flex justify-between">
              <span>Job No:</span>
              <span className="font-bold">{job.jobNumber}</span>
            </div>
            {receiptConfig.showDate && (
              <div className="flex justify-between">
                <span>Date:</span>
                <span>{new Date(job.createdAt).toLocaleDateString()}</span>
              </div>
            )}
            {receiptConfig.showName && (
              <div className="flex justify-between">
                <span>Customer:</span>
                <span className="font-bold">{job.customer.name}</span>
              </div>
            )}
            {receiptConfig.showPhone && job.customer.phone && (
              <div className="flex justify-between">
                <span>Phone:</span>
                <span>{job.customer.phone}</span>
              </div>
            )}
          </div>

          {receiptConfig.showRepairItem && (
            <div className="text-[10px] border-b pb-2 mb-4">
              <p className="font-bold uppercase mb-1">Device Details</p>
              <p>{job.deviceBrand} {job.deviceModel} ({job.deviceCategory})</p>
              {job.deviceImei && <p>IMEI: {job.deviceImei}</p>}
              {receiptConfig.showDefect && <p>Problem: {job.problemDescription}</p>}
            </div>
          )}

          <div className="text-[10px] border-b pb-2 mb-4">
            <p className="font-bold uppercase mb-1">Parts & Services</p>
            <div className="space-y-1">
              {job.jobItems && job.jobItems.map((item) => (
                <div key={item.id} className="flex justify-between">
                  <span>{item.product.name} (x{item.quantity})</span>
                  {receiptConfig.showRepairPrice && <span>{currencySymbol}{item.amount.toFixed(2)}</span>}
                </div>
              ))}
              {job.labourCost > 0 && (
                <div className="flex justify-between pt-1 mt-1 border-t border-dashed">
                  <span>Labour Charges</span>
                  {receiptConfig.showRepairPrice && <span>{currencySymbol}{job.labourCost.toFixed(2)}</span>}
                </div>
              )}
              {(!job.jobItems || job.jobItems.length === 0) && (!job.labourCost || job.labourCost === 0) && (
                <p className="text-gray-500 italic text-[9px]">No items charged.</p>
              )}
            </div>
          </div>

          <div className="text-[10px] border-b pb-2 mb-4">
            {receiptConfig.showTotalAmount && (
              <div className="flex justify-between">
                <span>Grand Total:</span>
                <span className="font-bold">{currencySymbol}{(job.actualCost || (spareCost + job.labourCost)).toFixed(2)}</span>
              </div>
            )}
            {receiptConfig.showTotalPaid && (
              <div className="flex justify-between">
                <span>Advance Paid:</span>
                <span className="font-bold text-green-600">{currencySymbol}{job.advanceAmount.toFixed(2)}</span>
              </div>
            )}
            {receiptConfig.showBalanceDue && (
              <div className="flex justify-between text-[12px] mt-1 pt-1 border-t border-dashed">
                <span className="font-bold uppercase">Balance:</span>
                <span className="font-bold text-red-600">{currencySymbol}{balance.toFixed(2)}</span>
              </div>
            )}
          </div>

          {receiptConfig.showTerms && (() => {
            let termsArray = [];
            if (companySettings?.terms) {
              try {
                const parsedTerms = JSON.parse(companySettings.terms);
                if (Array.isArray(parsedTerms) && parsedTerms.length > 0) {
                  termsArray = parsedTerms;
                }
              } catch (e) { }
            }
            if (termsArray.length === 0) {
              termsArray = [
                "We will not be held Liable for any loss of Data in case of software problem",
                "Collect Your Mobile within 15 days after complete your device repair"
              ];
            }
            return (
              <div className="text-left text-[8px] mt-2 border-t border-dashed pt-2 space-y-0.5 mb-2">
                <p className="font-bold text-[9px] uppercase text-center mb-1">Terms & Conditions</p>
                {termsArray.map((term, index) => (
                  <p key={index}>{index + 1}. {term}</p>
                ))}
              </div>
            );
          })()}

          {receiptConfig.showFooterMessage && (
            <div className="text-center text-[8px] mt-4">
              <p>Terms & Conditions Apply</p>
              <p className="mt-1 uppercase font-bold text-[10px]">{receiptConfig.footerMessage}</p>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default JobDetails;
